import {
  extractKnownTld,
  normalizeDomain,
  transformSpecialCharacters,
  splitTldFromDomain,
  isValidUrl,
} from '../../modules/inputTransformator';
import { AppState } from '../reducers';
import * as blobApi from '../../modules/blobApi';
import { FREE, PRODUCT, TRANSFER } from '../reducers/domain-field';
import { generalRequestFail } from './errors';
import { Action, CallAsync } from '../../definitions/types';
import { ThunkAction, Dispatch } from 'redux-thunk';
import * as Actions from './actions-consts';
import { DomainState } from '../reducers/domain';
import { createFreeWebsite } from './websiteCreation';
import { forwardToCheckout } from './checkout';

export enum FreeDomainHint {
  TldDetected = 1,
  Transformed,
}

export enum ProductDomainHint {
  Transferable = 10,
  Transformed,
}

export enum TransferDomainHint {
  Transferable = 10,
}

export function usernameValueChanged(username: string): Action<string> {
  return {
    type: Actions.USERNAME_VALUE_CHANGED,
    payload: username,
  };
}

export interface DomainFieldPayload {
  name: string;
  value: string;
}

export function domainFieldValueChanged(
  domainType: string,
  value: string
): Action<DomainFieldPayload> {
  return {
    type: Actions.DOMAIN_FIELD_VALUE_CHANGED,
    payload: {
      name: domainType,
      value,
    },
  };
}

export function tldChanged(tld: string): Action<string> {
  return {
    type: Actions.TLD_VALUE_CHANGED,
    payload: tld,
    meta: {
      tracked: { action: 'tld-value', label: 'change' },
    },
  };
}

export function activeDomainTypeSelected(domainType: string): Action<string> {
  return {
    type: Actions.ACTIVE_DOMAIN_TYPE_SELECTED,
    payload: domainType,
    meta: {
      tracked: { action: 'active-domain-type', label: 'select' },
    },
  };
}

export function validateUsername(): ThunkAction<void, AppState, CallAsync> {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState, callAsync: CallAsync) => {
    const { domain, options } = getState();

    dispatch(usernameValidationRequested());

    const freeValue = domain.fields.free.value;

    // transform username to a valid state
    const normalizedUsernameValue = normalizeDomain(freeValue);
    const transformedUsername = transformSpecialCharacters(normalizedUsernameValue);

    if (freeValue !== transformedUsername) {
      dispatch(usernameTransformed(freeValue, transformedUsername));
    }

    if (normalizedUsernameValue !== transformedUsername) {
      dispatch(showFreeDomainHint(FreeDomainHint.Transformed));
    }

    // TODO: maybe remove the extracted known tld if found
    const extractedTld = extractKnownTld(freeValue);

    if (extractedTld) {
      dispatch(showFreeDomainHint(FreeDomainHint.TldDetected));
    }

    // validate if username is actually free
    try {
      const response = await callAsync(
        blobApi.validateSignupData,
        { name: transformedUsername },
        options.lang
      );
      const error = response.errors ? response.errors.name : undefined;

      // dispatch that username was validated
      dispatch(usernameValidated({ value: transformedUsername, error }));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(error);
      }
      dispatch(generalRequestFail());
    }
  };
}

export function usernameValidationRequested(): Action<void> {
  return {
    type: Actions.USERNAME_VALIDATION_REQUESTED,
    meta: {
      tracked: { action: 'username-validation', label: 'request' },
    },
  };
}

export interface UsernameValidationPayload {
  value: string;
  error: string;
}

export function usernameValidated(
  payload: UsernameValidationPayload
): Action<UsernameValidationPayload> {
  return {
    type: Actions.USERNAME_VALUE_VALIDATED,
    payload,
    meta: {
      tracked: { action: 'username-value', label: 'validate' },
    },
  };
}

// !!!  IMPORTANT  !!!
// this action is for tracking purposes only - you will not find a usage of the constant in the code
// we also want to track the payload and therefore we have the payload in this action for nothing else
export interface UsernameTransformedPayload {
  raw: string;
  transformed: string;
}

export function usernameTransformed(
  rawUsername: string,
  transformedUsername: string
): Action<UsernameTransformedPayload> {
  return {
    type: Actions.USERNAME_VALUE_TRANSFORMED,
    payload: {
      raw: rawUsername,
      transformed: transformedUsername,
    },
    meta: {
      tracked: { action: 'username-value', label: 'transform' },
    },
  };
}

export interface DomainTransformedPayload {
  value: string;
  transformedValue: string;
}

export function domainTransformed(
  value: string,
  transformedValue: string
): Action<DomainTransformedPayload> {
  return {
    type: Actions.DOMAIN_VALUE_TRANSFORMED,
    payload: {
      value,
      transformedValue,
    },
    meta: {
      tracked: { action: 'domain-value', label: 'transform' },
    },
  };
}

export interface TldExtractedPayload {
  value: string;
  tld: string;
}

export function tldExtracted(value: string, tld: string): Action<TldExtractedPayload> {
  return {
    type: Actions.PRODUCT_TLD_EXTRACTED,
    payload: {
      value,
      tld,
    },
    meta: {
      tracked: { action: 'product-tld', label: 'extract' },
    },
  };
}

export interface DomainValidationRequestedPayload {
  url: string;
  activeType: string;
}

export function domainValidationRequested(
  url: string,
  activeType: string
): Action<DomainValidationRequestedPayload> {
  return {
    type: Actions.DOMAIN_VALIDATION_REQUESTED,
    payload: {
      url,
      activeType,
    },
    meta: {
      tracked: { action: 'domain-validation', label: 'request' },
    },
  };
}

export interface DomainValidationValidatedPayload {
  domain: string;
  domainFree: boolean;
  status?: string;
  error?: string;
  domainType: string;
}

export function domainValidationValidated(
  payload: DomainValidationValidatedPayload
): Action<DomainValidationValidatedPayload> {
  return {
    type: Actions.DOMAIN_VALIDATION_VALIDATED,
    payload,
    meta: {
      tracked: { action: 'domain-validation', label: 'validate' },
    },
  };
}

interface DomainTypeState {
  value: string;
  tld?: string;
}

function getStateByDomainType(domainType: string, state: DomainState): DomainTypeState {
  if (domainType === PRODUCT) {
    return {
      value: state.fields.product.value,
      tld: state.fields.product.tld,
    };
  }

  return {
    value: state.fields.transfer.value,
  };
}

function isDomainTransferable(status: string): boolean {
  return status === blobApi.DomainCheckReponseCodes.DOMAIN_IS_TRANSFERABLE;
}

function getErrorByStatusCode(domainType: string, status: string): string | null {
  if (domainType === PRODUCT) {
    switch (status) {
      case blobApi.DomainCheckReponseCodes.DOMAIN_IS_KNOWN_TO_US:
      case blobApi.DomainCheckReponseCodes.DOMAIN_IS_INVALID:
      case blobApi.DomainCheckReponseCodes.DOMAIN_IS_BLACKLISTED:
      case blobApi.DomainCheckReponseCodes.DOMAIN_IS_TRANSFERABLE:
      case blobApi.DomainCheckReponseCodes.DOMAIN_CHECK_FAILED:
      case blobApi.DomainCheckReponseCodes.DOMAIN_INVALID_AS_PRIMARY_DOMAIN:
      case blobApi.DomainCheckReponseCodes.UNKNOWN:
        return `signup-flow.domain.product.errors.${status.toLowerCase()}`;
      default:
        // no error
        return null;
    }
  }

  // domainType === TRANSFER
  switch (status) {
    case blobApi.DomainCheckReponseCodes.DOMAIN_IS_KNOWN_TO_US:
    case blobApi.DomainCheckReponseCodes.TLD_NOT_AVAILABLE:
    case blobApi.DomainCheckReponseCodes.DOMAIN_INVALID_AS_PRIMARY_DOMAIN:
    case blobApi.DomainCheckReponseCodes.DOMAIN_IS_INVALID:
    case blobApi.DomainCheckReponseCodes.DOMAIN_IS_BLACKLISTED:
    case blobApi.DomainCheckReponseCodes.DOMAIN_CHECK_FAILED:
    case blobApi.DomainCheckReponseCodes.UNKNOWN:
    case blobApi.DomainCheckReponseCodes.TLD_AVAILABLE_AT_PARTNER:
      return `signup-flow.domain.transfer.errors.${status.toLowerCase()}`;
    default:
      // no error
      return null;
  }
}

export function validateDomain(
  activeType: string
  // tslint:disable-next-line no-any
): ThunkAction<Promise<any>, AppState, CallAsync> {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState, callAsync: CallAsync) => {
    const {
      options: { lang },
      ...state
    } = getState();
    const domainState = getStateByDomainType(activeType, state.domain);
    const skipRemoteValidation = !domainState.value;

    if (skipRemoteValidation) {
      dispatch(
        domainValidationValidated({
          domainType: activeType,
          domainFree: false,
          error: `signup-flow.domain.${activeType}.errors.empty`,
          domain: '',
        })
      );
      return;
    }

    const normalizedDomainValue = normalizeDomain(domainState.value);
    let finalDomainValue = normalizedDomainValue;
    if (activeType === PRODUCT) {
      const { domain: domainWithoutTld, tld: extractedTld } = splitTldFromDomain(
        normalizedDomainValue
      );
      if (domainWithoutTld !== normalizedDomainValue) {
        dispatch(tldExtracted(domainWithoutTld, extractedTld));
      }

      const domainWithoutSpecialChars = transformSpecialCharacters(domainWithoutTld);
      if (domainWithoutSpecialChars !== domainWithoutTld) {
        dispatch(domainTransformed(domainWithoutTld, domainWithoutSpecialChars));
        dispatch(showProductDomainHint(ProductDomainHint.Transformed));
      }
      const tld = extractedTld || domainState.tld;
      finalDomainValue = domainWithoutSpecialChars + tld;
    }

    if (activeType === TRANSFER && !isValidUrl(normalizedDomainValue)) {
      dispatch(
        domainValidationValidated({
          domainType: activeType,
          domainFree: false,
          error: `signup-flow.domain.${activeType}.errors.url-not-valid`,
          domain: normalizedDomainValue,
        })
      );
      return;
    }

    dispatch(domainValidationRequested(finalDomainValue, activeType));

    try {
      const payload = await callAsync(blobApi.validateDomain, finalDomainValue, lang);
      const { domain, domainFree, status } = payload;
      const error = getErrorByStatusCode(activeType, status);

      if (activeType === PRODUCT && isDomainTransferable(status)) {
        dispatch(showProductDomainHint(ProductDomainHint.Transferable));
      }

      // SHOW ALWAYS HINT IF TRANSFER
      if (activeType === TRANSFER && !error) {
        dispatch(showTransferDomainHint(TransferDomainHint.Transferable));
      }

      dispatch(
        domainValidationValidated({
          domainType: activeType,
          domain,
          domainFree,
          status,
          error,
        })
      );
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(error);
      }
      dispatch(generalRequestFail());
    }
  };
}
// tslint:disable-next-line no-any
function continuingWithDomain(): Action<any> {
  return {
    type: Actions.CONTINUING_WITH_DOMAIN,
    meta: {
      tracked: { action: 'domain', label: 'continue' },
    },
  };
}

export function continueWithDomain(): ThunkAction<void, AppState, void> {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch(continuingWithDomain());
    const {
      domain: { activeType },
    } = getState();

    switch (activeType) {
      case FREE:
        return dispatch(createFreeWebsite());
      case PRODUCT:
        return dispatch(forwardToCheckout());
      case TRANSFER:
        return dispatch(forwardToCheckout());
    }
  };
}

export function showFreeDomainHint(hint: FreeDomainHint): Action<FreeDomainHint> {
  return {
    type: Actions.SHOW_FREE_DOMAIN_HINT,
    payload: hint,
    meta: {
      tracked: { action: 'free-domain-hint', label: 'show' },
    },
  };
}

export function showProductDomainHint(hint: ProductDomainHint): Action<ProductDomainHint> {
  return {
    type: Actions.SHOW_PRODUCT_DOMAIN_HINT,
    payload: hint,
    meta: {
      tracked: { action: 'product-domain-hint', label: 'show' },
    },
  };
}

export function showTransferDomainHint(hint: TransferDomainHint): Action<TransferDomainHint> {
  return {
    type: Actions.SHOW_TRANSFER_DOMAIN_HINT,
    payload: hint,
    meta: {
      tracked: { action: 'transfer-domain-hint', label: 'show' },
    },
  };
}
